import { styled } from '@mui/material';
import { useJoyride } from 'contexts/joyride';
import { useSnackbar } from 'contexts/snackbar';
import { useViewport } from 'contexts/viewport';
import { Box, Card, Dropdown, Icon, IconButton, Stack, Text } from 'shell/components';

const CourseCard = ({ course, handleCourseCardDropdown, onClick, ...rest }) => {
    const { joyrideRun } = useJoyride();
    const { message } = useSnackbar();
    const { device } = useViewport();

    const handleClickCopyCourseID = async (e) => {
        e.stopPropagation();
        await navigator.clipboard.writeText(course.generated_id);
        message.success('Course ID copied to clipboard.');
    };

    return (
        <RootBox device={device} {...rest}>
            <CustomCard onClick={() => onClick(course)}>
                <Stack sx={{ width: '100%', height: '100%' }}>
                    <TopStack direction={'row'} justifyContent={'space-between'} spacing={2}>
                        <Stack sx={{ minWidth: 0 }} spacing={1}>
                            <Text
                                component={'p'}
                                noWrap
                                title={course.name || 'No Name'}
                                variant={'h6'}
                                weight={'semibold'}
                            >
                                {course.name || 'No Name'}
                            </Text>
                            {course.finished_course_set_up === false && course?.role === 'instructor' && (
                                <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                    <Icon kind={'fileTypeUndefined'} />
                                    <Text color={'light'}>draft</Text>
                                </Stack>
                            )}
                            <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                <Text weight={'semibold'}>Course ID</Text>
                                <Text color={'light'}>{course.generated_id || ''}</Text>
                                {course.generated_id && (
                                    <IconButton
                                        aria-label={'copy course id'}
                                        kind={'copy'}
                                        size={'small'}
                                        variant={'square'}
                                        onClick={handleClickCopyCourseID}
                                    />
                                )}
                            </Stack>
                        </Stack>
                        {course.role === 'instructor' && (
                            <Dropdown
                                id={`course-card-dropdown-${course.course_id}`}
                                options={[
                                    { key: 'duplicate-course', value: 'Duplicate course' },
                                    { key: 'delete', value: 'Delete course' },
                                ]}
                                rootComponent={
                                    <IconButton
                                        aria-label={'more options'}
                                        disabled={joyrideRun}
                                        kind={'moreHorizontal'}
                                        size={'small'}
                                        variant={'square'}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                }
                                onSelect={({ key }) => handleCourseCardDropdown({ key, courseId: course.course_id })}
                            />
                        )}
                    </TopStack>
                    <BottomStack>
                        <Stack spacing={2}>
                            <GridStack>
                                <Text weight={'medium'}>Role</Text>
                                <CapitalizeFirstLetterText>{course.role}</CapitalizeFirstLetterText>
                            </GridStack>
                            <GridStack>
                                <Text weight={'medium'}>School</Text>
                                <CapitalizeFirstLetterText>{course.school}</CapitalizeFirstLetterText>
                            </GridStack>
                            <GridStack>
                                <Text weight={'medium'}>Department</Text>
                                <CapitalizeFirstLetterText>{course.faculty}</CapitalizeFirstLetterText>
                            </GridStack>
                            <GridStack>
                                <Text weight={'medium'}>Section</Text>
                                <Text>{course.section}</Text>
                            </GridStack>
                        </Stack>
                    </BottomStack>
                </Stack>
            </CustomCard>
        </RootBox>
    );
};

const RootBox = styled(Box)(({ device }) => {
    return {
        ...(device === 'mobile' && {
            width: '100%',
            height: '260px',
        }),
        ...(device === 'tablet' && {
            width: '320px',
            height: '244px',
        }),
        ...(device === 'desktop' && {
            width: '440px',
            height: '244px',
        }),
    };
});

const CustomCard = styled(Card)(() => {
    return {
        width: '100%',
        height: '100%',
        padding: 0,
    };
});

const TopStack = styled(Stack)(({ theme }) => {
    return {
        borderRadius: '8px 8px 0 0',
        height: '45%',
        padding: theme.spacing(5),
        background: theme.palette.ensightful.primary[50],
    };
});

const BottomStack = styled(Stack)(({ theme }) => {
    return {
        height: '55%',
        padding: theme.spacing(5),
    };
});

const GridStack = styled(Stack)(() => {
    return {
        display: 'grid',
        gridTemplateColumns: '94px 1fr',
    };
});

const CapitalizeFirstLetterText = styled(Text)(() => {
    return {
        textTransform: 'capitalize',
    };
});

export default CourseCard;
